import {
  Avatar,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import Select from "@mui/material/Select";
import { useState } from "react";
import { DamsAppAsset } from "../../assets/registry";
import { DamsColors } from "../styles/DamsColors";
import type { DamsMultiSelectInputProps } from "./index";

export function MultiSelectInput({
  size = "short",
  items,
  label,
  placeholder = "Select options",
  handleChange,
  name,
  required = false,
  value = [],
  error,
  errorMessage,
  loading,
}: Readonly<DamsMultiSelectInputProps>) {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    if (!loading) {
      setOpen(!open);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const selectedValues = event.target.value;

    const values =
      typeof selectedValues === "string" ? [selectedValues] : selectedValues;

    if (handleChange) {
      handleChange(values);
    }
  };

  const InputSize =
    size === "long"
      ? { width: 512, height: 75 }
      : size === "full"
        ? { width: "100%", height: 75 }
        : { width: 260, height: 75 };

  const renderValue = (selected: string[]) => {
    if (!selected || selected.length === 0) {
      return (
        <Typography
          color={DamsColors.Text.SecondaryText}
          fontFamily={"Inter"}
          fontWeight={330}
        >
          {placeholder}
        </Typography>
      );
    }

    return (
      <Typography
        color={DamsColors.Text.PrimaryText}
        fontFamily={"Inter"}
        fontWeight={400}
      >
        {selected.length} item{selected.length !== 1 ? "s" : ""} selected
      </Typography>
    );
  };

  const selectId = `multiselect-${name || "input"}-${Math.random()
    .toString(36)
    .substring(2, 11)}`;

  return (
    <Stack
      width={InputSize.width}
      gap={0.2}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "flex-end",
        minHeight: "80px",
      }}
    >
      {label && (
        <label
          htmlFor={selectId}
          style={{
            marginBottom: "0.25rem",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "0.9rem",
            color: DamsColors.Text.PrimaryText,
            lineHeight: 1.2,
          }}
        >
          {label}{" "}
          {required && (
            <span
              style={{
                color: "#ff0000",
              }}
            >
              *
            </span>
          )}
        </label>
      )}

      <FormControl sx={{ flexShrink: 0 }}>
        <Select
          id={selectId}
          name={name}
          multiple
          value={value}
          open={open}
          onChange={handleSelectChange}
          onClose={handleClose}
          onClick={handleToggle}
          displayEmpty
          renderValue={renderValue}
          IconComponent={CustomEmptyIcon}
          endAdornment={
            <IconButton onClick={handleToggle}>
              {loading ? (
                <CircularProgress size={15} />
              ) : (
                <Avatar
                  src={DamsAppAsset.icons.select_arrow}
                  sx={styles.arrowDownIcon}
                />
              )}
            </IconButton>
          }
          MenuProps={{
            disableAutoFocusItem: true,
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          sx={{
            ...styles.selectInput,
            minHeight: 55,
            height: "auto",
            paddingY: value.length > 0 ? 0.5 : 0,
          }}
        >
          {items.map((data) => (
            <MenuItem
              value={data.value}
              key={data.value}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                const currentIndex = value.indexOf(data.value);
                const newValue = [...value];

                if (currentIndex === -1) {
                  newValue.push(data.value);
                } else {
                  newValue.splice(currentIndex, 1);
                }

                if (handleChange) {
                  handleChange(newValue);
                }
              }}
            >
              <Checkbox
                checked={value.indexOf(data.value) > -1}
                sx={{
                  color: DamsColors.Text.SecondaryText,
                  "&.Mui-checked": {
                    color: DamsColors.Button.Primary,
                  },
                }}
              />
              <ListItemText
                primary={
                  <Typography
                    fontFamily={"Inter"}
                    fontWeight={400}
                    fontSize={16}
                    color={DamsColors.Text.PrimaryText}
                  >
                    {data.label}
                  </Typography>
                }
              />
            </MenuItem>
          ))}
        </Select>
        {error && (
          <Typography variant="caption" color={"red"} sx={{ marginLeft: 1 }}>
            {errorMessage}
          </Typography>
        )}
      </FormControl>
    </Stack>
  );
}

const CustomEmptyIcon = () => null;

const styles = {
  arrowDownIcon: {
    width: "20px",
    height: "20px",
  },

  selectInput: {
    borderRadius: "8px",
    borderColor: `${DamsColors.Border.PageInput} !important`,

    "& .MuiSelect-select": {
      borderRadius: "8px",
      color: "#667085",
      backgroundColor: "white",
      padding: "0.625rem",
      minHeight: "unset",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "4px",

      "&:focus": {
        borderRadius: "8px",
        borderColor: `${DamsColors.Border.PageInput} !important`,
      },

      "&:hover": {
        borderRadius: "8px",
        borderColor: `${DamsColors.Border.PageInput} !important`,
      },

      "&:active": {
        borderRadius: "8px",
        borderColor: `${DamsColors.Border.PageInput} !important`,
      },
      borderColor: `${DamsColors.Border.PageInput} !important`,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1.5px",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${DamsColors.Border.PageInput} !important`,
      borderWidth: "2px",
      borderRadius: "8px",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${DamsColors.Border.PageInput} !important`,
      borderRadius: "8px",
    },
  },
};

export default MultiSelectInput;
